import React from 'react';
import NavBar from '@/components/NavBar';
import CompressionHistory from '@/components/CompressionHistory';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { History as HistoryIcon, Crown, Lock, Zap, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { PRICING } from '@/constants/pricing';

const HistoryPaywall: React.FC = () => {
  const { createCheckout } = useSubscription();
  const navigate = useNavigate();

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
          <HistoryIcon className="w-8 h-8 text-primary" />
          Historial de Compresión
        </h1>
        <p className="text-muted-foreground">
          Revisa todas tus compresiones anteriores y estadísticas
        </p>
      </div>

      {/* Premium Required Card */}
      <div className="max-w-2xl mx-auto">
        <Card className="border-2 border-dashed border-primary/20">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Función Premium Requerida</CardTitle>
            <CardDescription className="text-lg">
              El historial de compresión está disponible solo para usuarios Premium
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Features List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Historial completo de compresiones",
                "Estadísticas detalladas",
                "Exportar datos de compresión",
                "Análisis de ahorro de espacio",
                "Seguimiento de configuraciones",
                "Datos históricos ilimitados"
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                >
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm font-medium">{feature}</span>
                </div>
              ))}
            </div>

            {/* Premium Benefits */}
            <div className="bg-gradient-to-r from-purple-600/10 to-blue-600/10 p-6 rounded-xl border border-primary/20">
              <div className="flex items-center gap-3 mb-4">
                <Crown className="w-6 h-6 text-primary" />
                <h3 className="text-lg font-semibold">¿Por qué Premium?</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                El historial te permite rastrear tu productividad, optimizar tus configuraciones 
                y mantener un registro completo de todas tus compresiones.
              </p>
              <div className="flex items-center gap-2 text-sm text-primary">
                <Zap className="w-4 h-4" />
                <span>Acceso instantáneo al historial completo</span>
              </div>
            </div>

            {/* Upgrade Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={() => createCheckout('monthly')}
                className="w-full h-12 text-lg bg-primary hover:bg-primary/90"
              >
                <Crown className="w-5 h-5 mr-2" />
                Actualizar a Premium - {PRICING.MONTHLY.label.replace('Mensual - ', '')}
              </Button>
              <Button 
                onClick={() => createCheckout('annual')}
                variant="outline"
                className="w-full h-12 text-lg border-primary text-primary hover:bg-primary/10"
              >
                <Crown className="w-5 h-5 mr-2" />
                Plan Anual - ${PRICING.ANNUAL.price} {PRICING.ANNUAL.currency}/{PRICING.ANNUAL.period} (Ahorra {PRICING.ANNUAL.savings})
              </Button>
            </div>

            {/* Back to Home */}
            <div className="text-center pt-4 border-t">
              <Button 
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-muted-foreground hover:text-foreground"
              >
                ← Volver al Compresor
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const History: React.FC = () => {
  const { isPremium, loading } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Show loading state
  if (loading) {
    return (
      <>
        <NavBar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Verificando suscripción...</p>
          </div>
        </div>
      </>
    );
  }

  // Redirect unauthenticated users
  if (!user) {
    return (
      <>
        <NavBar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center">
              <HistoryIcon className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-bold">Inicia Sesión Requerido</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Necesitas iniciar sesión para acceder a tu historial de compresión.
            </p>
            <Button onClick={() => navigate('/auth')} className="mt-4">
              <Zap className="w-4 h-4 mr-2" />
              Iniciar Sesión
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead 
        title="Historial de Compresión - Compresor de Imágenes | CompresorJPEG"
        description="Accede a tu historial completo de compresión de imágenes. Revisa estadísticas, configuraciones y el ahorro de espacio conseguido con CompresorJPEG."
        keywords="historial compresion imagenes, estadisticas jpg png, seguimiento optimizacion fotos, datos compression"
        canonical="https://compresorjpeg.com/history"
      />
      <NavBar />
      {isPremium ? <CompressionHistory /> : <HistoryPaywall />}
    </>
  );
};

export default History;